import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

// ============================================
// Types
// ============================================

interface TierBreakdown {
  key: string;
  displayName: string;
  monthlyPricePence: number | null;
  activeCount: number;
  mrrPence: number | null;
  includedMonitoring: number;
  includedJobs: number;
}

interface NeedsAttentionRow {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  plan_label: string;
  created_at?: string;
  last_login_at?: string | null;
  days_inactive?: number;
  used?: number;
  quota?: number;
  pct_used?: number;
}

interface InvoiceRevenueBucket {
  pence: number;
  count: number;
}

interface MappingInvoices {
  available: boolean;
  reason?: string;
  thisMonth: InvoiceRevenueBucket;
  last30d: InvoiceRevenueBucket;
  thisYear: InvoiceRevenueBucket;
  allTime: InvoiceRevenueBucket;
  manual?: InvoiceRevenueBucket;
  source?: string;
  currency: string;
  cached?: boolean;
  truncated?: boolean;
}

interface Overview {
  generatedAt: string;
  revenue: {
    mrrPence: number;
    activePayingUsers: number;
    tierBreakdown: TierBreakdown[];
    mappingInvoices: MappingInvoices;
  };
  usage: {
    monitoring: { quotaSold: number; usedThisCycle: number; utilisationPct: number; activeSubs: number };
    jobs: { quotaSold: number; activeWatches: number; jobsCapturedThisMonth: number; utilisationPct: number };
    clay: { allTime: number; thisMonth: number; thisWeek: number };
    sentrion: { activeWatches: number; jobsCapturedThisMonth: number; externalBalance: number | null; externalBalanceCapturedAt: string | null; externalNote: string };
  };
  needsAttention: {
    paidNoProjects: NeedsAttentionRow[];
    dormantPaying: NeedsAttentionRow[];
    approachingQuota: NeedsAttentionRow[];
  };
}

interface TeamMember {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  teamRole: string | null;
  lastLoginAt: string | null;
  lastActivityAt: string | null;
  lastActive: string | null;
}

interface PayingUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  createdAt: string;
  lastLoginAt: string | null;
  teamLastActive: string | null;
  teamId: number | null;
  teamMemberCount: number;
  teamMembers: TeamMember[];
  stripeCustomerId: string | null;
  customerStatus: 'active' | 'trialing' | 'past_due' | 'cancelled' | 'grandfathered' | 'unknown';
  planLabel: string;
  tierKey: string | null;
  mrrPence: number;
  monitoringStatus: string | null;
  monitoringQuota: number;
  monitoringUsed: number;
  monitoringUtilisation: number | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean | null;
  projectCount: number;
  activeWatches: number;
  jobsCapturedThisMonth: number;
  clayEnrichmentsThisMonth: number;
}

interface StripeSubRow {
  stripeSubscriptionId: string;
  stripeCustomerId: string | null;
  userId: number | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  status: string;
  currentPeriodEnd: string | null;
  canceledAt: string | null;
  amountPence: number | null;
  currency: string | null;
}

interface StripeHealth {
  available: boolean;
  reason?: string;
  pastDue: StripeSubRow[];
  recentCancellations: StripeSubRow[];
  failedInvoicesLast30d: number;
  cached?: boolean;
  cachedAgeMs?: number;
}

// ============================================
// Helpers
// ============================================

const formatPence = (pence: number | null | undefined): string => {
  if (pence == null) return '—';
  const pounds = pence / 100;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0
  }).format(pounds);
};

const formatNumber = (n: number | null | undefined): string => {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-GB').format(n);
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDaysAgo = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};

const nameOf = (row: { firstName?: string | null; lastName?: string | null; first_name?: string | null; last_name?: string | null; email?: string | null }): string => {
  const first = row.firstName ?? row.first_name;
  const last = row.lastName ?? row.last_name;
  const full = [first, last].filter(Boolean).join(' ').trim();
  return full || row.email || 'Unknown';
};

// ============================================
// Main Component
// ============================================

export const CommandCenter: React.FC = () => {
  const { user: authUser } = useAuth();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [users, setUsers] = useState<PayingUser[]>([]);
  const [stripeHealth, setStripeHealth] = useState<StripeHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userSort, setUserSort] = useState<'mrr' | 'utilisation' | 'lastLogin' | 'name'>('mrr');
  const [userFilter, setUserFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'cancelled' | 'grandfathered' | 'past_due'>('all');
  const [expandedAttention, setExpandedAttention] = useState<'none' | 'paidNoProjects' | 'dormant' | 'approachingQuota'>('none');
  const [expandedTeams, setExpandedTeams] = useState<Set<number>>(new Set());
  const [activeUsers, setActiveUsers] = useState<Array<{ id: number; email: string; firstName: string; lastName: string; teamName: string | null; secondsAgo: number }>>([]);
  const [activeMinutes, setActiveMinutes] = useState<number>(5);
  const [activeLoading, setActiveLoading] = useState(false);

  const toggleTeam = (userId: number) => {
    setExpandedTeams(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const [overviewRes, usersRes] = await Promise.all([
        fetch('/api/admin/command-center/overview', { headers: getAuthHeaders() }),
        fetch('/api/admin/command-center/users', { headers: getAuthHeaders() })
      ]);
      if (!overviewRes.ok) throw new Error('Failed to load overview');
      if (!usersRes.ok) throw new Error('Failed to load users');
      const overviewData = await overviewRes.json();
      const usersData = await usersRes.json();
      setOverview(overviewData);
      setUsers(usersData.users || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeHealth = async () => {
    setStripeLoading(true);
    try {
      const res = await fetch('/api/admin/command-center/stripe-health', { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load Stripe health');
      const data = await res.json();
      setStripeHealth(data);
    } catch (err: any) {
      setStripeHealth({ available: false, reason: err.message, pastDue: [], recentCancellations: [], failedInvoicesLast30d: 0 });
    } finally {
      setStripeLoading(false);
    }
  };

  const fetchActiveUsers = async (minutes: number) => {
    setActiveLoading(true);
    try {
      const res = await fetch(`/api/admin/command-center/active-users?minutes=${minutes}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load active users');
      const data = await res.json();
      setActiveUsers(data.users || []);
    } catch {
      setActiveUsers([]);
    } finally {
      setActiveLoading(false);
    }
  };

  const formatSecondsAgo = (s: number): string =>
    s < 60 ? `${Math.max(0, s)}s ago` : `${Math.floor(s / 60)}m ago`;

  useEffect(() => {
    fetchOverview();
    fetchStripeHealth();
  }, []);

  // Live "who's active" — refetch on range change and poll every 30s so the
  // panel stays current while you decide whether it's safe to deploy.
  useEffect(() => {
    fetchActiveUsers(activeMinutes);
    const id = setInterval(() => fetchActiveUsers(activeMinutes), 30000);
    return () => clearInterval(id);
  }, [activeMinutes]);

  // Filter + sort users
  const sortedUsers = useMemo(() => {
    let filtered = users;
    if (statusFilter !== 'all') {
      filtered = filtered.filter(u => u.customerStatus === statusFilter);
    }
    if (userFilter.trim()) {
      const f = userFilter.toLowerCase();
      filtered = filtered.filter(u =>
        u.email.toLowerCase().includes(f) ||
        nameOf(u).toLowerCase().includes(f) ||
        (u.planLabel || '').toLowerCase().includes(f)
      );
    }
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (userSort === 'mrr') return b.mrrPence - a.mrrPence;
      if (userSort === 'utilisation') return (b.monitoringUtilisation ?? -1) - (a.monitoringUtilisation ?? -1);
      if (userSort === 'lastLogin') {
        const aT = a.teamLastActive ? new Date(a.teamLastActive).getTime() : 0;
        const bT = b.teamLastActive ? new Date(b.teamLastActive).getTime() : 0;
        return bT - aT;
      }
      return nameOf(a).localeCompare(nameOf(b));
    });
    return sorted;
  }, [users, userSort, userFilter, statusFilter]);

  // Status counts for the filter chips
  const statusCounts = useMemo(() => {
    const counts = { all: users.length, active: 0, cancelled: 0, grandfathered: 0, past_due: 0, trialing: 0, unknown: 0 } as Record<string, number>;
    for (const u of users) counts[u.customerStatus] = (counts[u.customerStatus] || 0) + 1;
    return counts;
  }, [users]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading command center…</p>
        </div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-800">
            Failed to load command center. {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Command Center</h1>
              <p className="text-gray-600 mt-1">
                Live view across revenue, usage, and customer health.
                {' '}<span className="text-gray-400 text-sm">Last refreshed {formatDaysAgo(overview.generatedAt)}.</span>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.location.href = '/admin/crm'}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium"
              >
                CRM
              </button>
              <button
                onClick={() => { fetchOverview(); fetchStripeHealth(); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
              >
                Refresh
              </button>
              <button
                onClick={() => window.location.href = '/admin'}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              >
                Back to Admin
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Active now — who's live in the system (pre-deploy safety check) */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${activeUsers.length > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
              <h2 className="text-lg font-semibold text-gray-900">
                Active now — {activeUsers.length} {activeUsers.length === 1 ? 'user' : 'users'}
              </h2>
              <span className="text-xs text-gray-400">in the last {activeMinutes} min · auto-refreshes every 30s</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-md border border-gray-300 overflow-hidden">
                {[5, 10, 15, 30, 60].map(m => (
                  <button
                    key={m}
                    onClick={() => setActiveMinutes(m)}
                    className={`px-3 py-1 text-xs font-medium ${activeMinutes === m ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    {m}m
                  </button>
                ))}
              </div>
              <button
                onClick={() => fetchActiveUsers(activeMinutes)}
                disabled={activeLoading}
                className="px-3 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {activeLoading ? '…' : '↻ Refresh'}
              </button>
            </div>
          </div>
          {activeUsers.length === 0 ? (
            <p className="text-sm text-gray-500">No one active in the last {activeMinutes} minutes — safe to deploy.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {activeUsers.map(u => (
                <div key={u.id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}
                    </p>
                    <p className="text-xs text-gray-500">
                      {u.email}{u.teamName ? ` · ${u.teamName}` : ''}
                    </p>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{formatSecondsAgo(u.secondsAgo)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <KPITile
            label="Monthly Recurring Revenue"
            value={formatPence(overview.revenue.mrrPence)}
            sub={`${overview.revenue.activePayingUsers} paying customers`}
            tone="blue"
          />
          <KPITile
            label="Mapping Invoices (mo)"
            value={overview.revenue.mappingInvoices.available
              ? formatPence(overview.revenue.mappingInvoices.thisMonth.pence)
              : '—'}
            sub={overview.revenue.mappingInvoices.available
              ? `${overview.revenue.mappingInvoices.thisMonth.count} invoice${overview.revenue.mappingInvoices.thisMonth.count === 1 ? '' : 's'} this month`
              : 'Secondary Stripe key not set'}
            tone="green"
          />
          <KPITile
            label="Monitoring Utilisation"
            value={`${overview.usage.monitoring.utilisationPct}%`}
            sub={`${formatNumber(overview.usage.monitoring.usedThisCycle)} / ${formatNumber(overview.usage.monitoring.quotaSold)} this cycle`}
            tone={overview.usage.monitoring.utilisationPct > 80 ? 'red' : overview.usage.monitoring.utilisationPct > 60 ? 'amber' : 'green'}
          />
          <KPITile
            label="Job Watches Active"
            value={formatNumber(overview.usage.jobs.activeWatches)}
            sub={`${formatNumber(overview.usage.jobs.jobsCapturedThisMonth)} jobs captured this month`}
            tone="purple"
          />
          <KPITile
            label="Stripe Past Due"
            value={stripeLoading ? '…' : formatNumber(stripeHealth?.pastDue?.length ?? 0)}
            sub={stripeLoading ? 'fetching…' : `${stripeHealth?.failedInvoicesLast30d ?? 0} failed invoices last 30d`}
            tone={(stripeHealth?.pastDue?.length ?? 0) > 0 ? 'red' : 'gray'}
          />
        </div>

        {/* CRM widget — upcoming demos + tasks needing attention */}
        <CrmWidget />

        {/* Revenue / Tier Breakdown */}
        <Section title="Revenue by tier">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-gray-500 border-b">
              <tr>
                <th className="py-2 pr-4">Tier</th>
                <th className="py-2 pr-4 text-right">Active subs</th>
                <th className="py-2 pr-4 text-right">Price</th>
                <th className="py-2 pr-4 text-right">MRR contribution</th>
                <th className="py-2 pr-4 text-right">Monitoring/mo</th>
                <th className="py-2 pr-4 text-right">Jobs/mo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {overview.revenue.tierBreakdown.map(t => (
                <tr key={t.key}>
                  <td className="py-3 pr-4 font-medium text-gray-900">{t.displayName}</td>
                  <td className="py-3 pr-4 text-right text-gray-700">{t.activeCount}</td>
                  <td className="py-3 pr-4 text-right text-gray-700">{t.monthlyPricePence != null ? formatPence(t.monthlyPricePence) : 'varies'}</td>
                  <td className="py-3 pr-4 text-right font-medium text-gray-900">{t.mrrPence != null ? formatPence(t.mrrPence) : '—'}</td>
                  <td className="py-3 pr-4 text-right text-gray-600">{formatNumber(t.includedMonitoring)}</td>
                  <td className="py-3 pr-4 text-right text-gray-600">{formatNumber(t.includedJobs)}</td>
                </tr>
              ))}
              <tr className="font-semibold text-gray-900 bg-gray-50">
                <td className="py-3 pr-4">Total</td>
                <td className="py-3 pr-4 text-right">{overview.revenue.activePayingUsers}</td>
                <td className="py-3 pr-4 text-right"></td>
                <td className="py-3 pr-4 text-right">{formatPence(overview.revenue.mrrPence)}</td>
                <td className="py-3 pr-4 text-right">{formatNumber(overview.usage.monitoring.quotaSold)}</td>
                <td className="py-3 pr-4 text-right">{formatNumber(overview.usage.jobs.quotaSold)}</td>
              </tr>
            </tbody>
          </table>
        </Section>

        {/* Mapping data invoice revenue (secondary Stripe account) */}
        <Section title="Mapping data revenue (one-off invoices)">
          {!overview.revenue.mappingInvoices.available ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <p className="text-amber-900 font-medium mb-1">Secondary Stripe account not connected.</p>
              <p className="text-amber-700">
                Set <code className="bg-amber-100 px-1 rounded text-xs">STRIPE_INVOICES_SECRET_KEY</code> in <code className="bg-amber-100 px-1 rounded text-xs">backend/.env</code> with the secret key from your second "Marketmapr" Stripe account.
                Restart the API service and refresh — invoice totals will appear here.
              </p>
              {overview.revenue.mappingInvoices.reason && (
                <p className="text-xs text-amber-600 mt-1 italic">{overview.revenue.mappingInvoices.reason}</p>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <MiniRevenueCard label="This month" pence={overview.revenue.mappingInvoices.thisMonth.pence} count={overview.revenue.mappingInvoices.thisMonth.count} />
                <MiniRevenueCard label="Last 30 days" pence={overview.revenue.mappingInvoices.last30d.pence} count={overview.revenue.mappingInvoices.last30d.count} />
                <MiniRevenueCard label="This year" pence={overview.revenue.mappingInvoices.thisYear.pence} count={overview.revenue.mappingInvoices.thisYear.count} />
                <MiniRevenueCard label="All time" pence={overview.revenue.mappingInvoices.allTime.pence} count={overview.revenue.mappingInvoices.allTime.count} />
              </div>
              {overview.revenue.mappingInvoices.manual && overview.revenue.mappingInvoices.manual.count > 0 && (
                <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                  Includes <span className="font-semibold">{formatPence(overview.revenue.mappingInvoices.manual.pence)}</span> from {overview.revenue.mappingInvoices.manual.count} manual / BACS {overview.revenue.mappingInvoices.manual.count === 1 ? 'payment' : 'payments'} logged via the CRM.
                </div>
              )}
              <p className="text-xs text-gray-400 mt-3">
                Tracked separately from MRR. Includes Stripe (secondary 'invoices' account) + manual / BACS / Xero entries.
                {overview.revenue.mappingInvoices.cached && ' Cached up to 15 min.'}
              </p>
            </>
          )}
        </Section>

        {/* Stripe Health */}
        <Section title="Payment health (live from Stripe)">
          {stripeLoading ? (
            <p className="text-sm text-gray-500">Fetching from Stripe…</p>
          ) : !stripeHealth?.available ? (
            <p className="text-sm text-gray-500">Stripe API not available. {stripeHealth?.reason}</p>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MiniStat label="Past due subscriptions" value={stripeHealth.pastDue.length} tone={stripeHealth.pastDue.length > 0 ? 'red' : 'green'} />
                <MiniStat label="Cancellations (last 60d)" value={stripeHealth.recentCancellations.length} tone={stripeHealth.recentCancellations.length > 3 ? 'amber' : 'gray'} />
                <MiniStat label="Failed invoices (last 30d)" value={stripeHealth.failedInvoicesLast30d} tone={stripeHealth.failedInvoicesLast30d > 0 ? 'red' : 'green'} />
              </div>

              {stripeHealth.pastDue.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Past-due subscriptions</h4>
                  <div className="overflow-hidden rounded-lg border border-red-200">
                    <table className="w-full text-sm">
                      <thead className="bg-red-50 text-left text-xs uppercase text-red-800">
                        <tr>
                          <th className="py-2 px-3">Customer</th>
                          <th className="py-2 px-3">Amount</th>
                          <th className="py-2 px-3">Period ends</th>
                          <th className="py-2 px-3">Stripe ID</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100">
                        {stripeHealth.pastDue.map(r => (
                          <tr key={r.stripeSubscriptionId}>
                            <td className="py-2 px-3">
                              <div className="font-medium text-gray-900">{nameOf(r)}</div>
                              <div className="text-xs text-gray-500">{r.email}</div>
                            </td>
                            <td className="py-2 px-3 text-gray-700">{formatPence(r.amountPence)}</td>
                            <td className="py-2 px-3 text-gray-700">{formatDate(r.currentPeriodEnd)}</td>
                            <td className="py-2 px-3 text-xs text-gray-400 font-mono">{r.stripeSubscriptionId}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {stripeHealth.recentCancellations.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Recent cancellations (last 60 days)</h4>
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
                        <tr>
                          <th className="py-2 px-3">Customer</th>
                          <th className="py-2 px-3">Cancelled</th>
                          <th className="py-2 px-3">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {stripeHealth.recentCancellations.slice(0, 20).map(r => (
                          <tr key={r.stripeSubscriptionId}>
                            <td className="py-2 px-3">
                              <div className="font-medium text-gray-900">{nameOf(r)}</div>
                              <div className="text-xs text-gray-500">{r.email}</div>
                            </td>
                            <td className="py-2 px-3 text-gray-700">{formatDate(r.canceledAt)}</td>
                            <td className="py-2 px-3 text-gray-700">{formatPence(r.amountPence)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {stripeHealth.cached && (
                <p className="text-xs text-gray-400">Cached {Math.round((stripeHealth.cachedAgeMs ?? 0) / 1000)}s ago — refreshes every 5 minutes.</p>
              )}
            </div>
          )}
        </Section>

        {/* Usage breakdown */}
        <Section title="Platform usage">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <UsageCard
              label="Monitoring"
              primary={`${overview.usage.monitoring.utilisationPct}%`}
              primaryLabel="utilisation"
              rows={[
                { label: 'Quota sold (cycle)', value: formatNumber(overview.usage.monitoring.quotaSold) },
                { label: 'Used this cycle', value: formatNumber(overview.usage.monitoring.usedThisCycle) },
                { label: 'Active subs', value: formatNumber(overview.usage.monitoring.activeSubs) }
              ]}
            />
            <UsageCard
              label="Jobs (Sentrion)"
              primary={formatNumber(overview.usage.jobs.activeWatches)}
              primaryLabel="active watches"
              rows={[
                { label: 'Quota sold', value: formatNumber(overview.usage.jobs.quotaSold) },
                { label: 'Jobs captured (month)', value: formatNumber(overview.usage.jobs.jobsCapturedThisMonth) },
                { label: 'Utilisation', value: `${overview.usage.jobs.utilisationPct}%` }
              ]}
            />
            <UsageCard
              label="Clay enrichments"
              primary={formatNumber(overview.usage.clay.thisMonth)}
              primaryLabel="this month"
              rows={[
                { label: 'This week', value: formatNumber(overview.usage.clay.thisWeek) },
                { label: 'All time', value: formatNumber(overview.usage.clay.allTime) }
              ]}
            />
            <UsageCard
              label="Sentrion API"
              primary={overview.usage.sentrion.externalBalance != null ? formatNumber(overview.usage.sentrion.externalBalance) : 'no data yet'}
              primaryLabel="credits remaining"
              rows={[
                { label: 'Active watches', value: formatNumber(overview.usage.sentrion.activeWatches) },
                { label: 'Jobs this month', value: formatNumber(overview.usage.sentrion.jobsCapturedThisMonth) },
                ...(overview.usage.sentrion.externalBalanceCapturedAt
                  ? [{ label: 'Last captured', value: formatDaysAgo(overview.usage.sentrion.externalBalanceCapturedAt) }]
                  : [])
              ]}
              footnote={overview.usage.sentrion.externalNote}
            />
          </div>
        </Section>

        {/* Needs Attention */}
        <Section title="Needs attention">
          <div className="space-y-3">
            <AttentionGroup
              title="Paid but no projects yet"
              count={overview.needsAttention.paidNoProjects.length}
              tone="red"
              expanded={expandedAttention === 'paidNoProjects'}
              onToggle={() => setExpandedAttention(expandedAttention === 'paidNoProjects' ? 'none' : 'paidNoProjects')}
              rows={overview.needsAttention.paidNoProjects}
              renderRow={r => (
                <>
                  <span className="text-xs text-gray-500">{r.plan_label}</span>
                  <span>Signed up {formatDate(r.created_at)}</span>
                </>
              )}
            />
            <AttentionGroup
              title="Dormant paying users (14+ days no login)"
              count={overview.needsAttention.dormantPaying.length}
              tone="amber"
              expanded={expandedAttention === 'dormant'}
              onToggle={() => setExpandedAttention(expandedAttention === 'dormant' ? 'none' : 'dormant')}
              rows={overview.needsAttention.dormantPaying}
              renderRow={r => (
                <>
                  <span className="text-xs text-gray-500">{r.plan_label}</span>
                  <span>{r.days_inactive}d inactive</span>
                </>
              )}
            />
            <AttentionGroup
              title="Approaching monitoring quota (70%+ used)"
              count={overview.needsAttention.approachingQuota.length}
              tone="amber"
              expanded={expandedAttention === 'approachingQuota'}
              onToggle={() => setExpandedAttention(expandedAttention === 'approachingQuota' ? 'none' : 'approachingQuota')}
              rows={overview.needsAttention.approachingQuota}
              renderRow={r => (
                <>
                  <span className="text-xs text-gray-500">{r.plan_label}</span>
                  <span>{r.pct_used}% — {formatNumber(r.used)} / {formatNumber(r.quota)}</span>
                </>
              )}
            />
          </div>
        </Section>

        {/* Customer drill-down — includes active, cancelled, past-due, grandfathered */}
        <Section title={`All customers (${users.length})`}>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {(['all', 'active', 'cancelled', 'past_due', 'grandfathered'] as const).map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {s === 'all' ? 'All' : s === 'past_due' ? 'Past due' : s.charAt(0).toUpperCase() + s.slice(1)}
                <span className="ml-1.5 opacity-70">{statusCounts[s] ?? 0}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 mb-4">
            <input
              type="text"
              placeholder="Filter by name, email, or plan"
              value={userFilter}
              onChange={e => setUserFilter(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <select
              value={userSort}
              onChange={e => setUserSort(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="mrr">Sort: MRR (high → low)</option>
              <option value="utilisation">Sort: Monitoring utilisation</option>
              <option value="lastLogin">Sort: Last login (recent first)</option>
              <option value="name">Sort: Name</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500 border-b">
                <tr>
                  <th className="py-2 pr-3"></th>
                  <th className="py-2 pr-3">Customer</th>
                  <th className="py-2 pr-3">Plan</th>
                  <th className="py-2 pr-3 text-right">MRR</th>
                  <th className="py-2 pr-3 text-right">Projects</th>
                  <th className="py-2 pr-3 text-right">Monitoring</th>
                  <th className="py-2 pr-3 text-right">Watches</th>
                  <th className="py-2 pr-3 text-right">Jobs (mo)</th>
                  <th className="py-2 pr-3 text-right">Clay (mo)</th>
                  <th className="py-2 pr-3 text-right">Last activity</th>
                  <th className="py-2 pr-3 text-right">Renewal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedUsers.map(u => {
                  const isExpanded = expandedTeams.has(u.id);
                  const hasTeam = u.teamMemberCount > 0;
                  return (
                    <React.Fragment key={u.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="py-3 pr-2 align-top">
                          {hasTeam ? (
                            <button
                              onClick={() => toggleTeam(u.id)}
                              className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-700"
                              title={`${u.teamMemberCount} team member${u.teamMemberCount === 1 ? '' : 's'}`}
                            >
                              {isExpanded ? '▾' : '▸'}
                            </button>
                          ) : null}
                        </td>
                        <td className="py-3 pr-3">
                          <div className="font-medium text-gray-900">{nameOf(u)}</div>
                          <div className="text-xs text-gray-500">{u.email}</div>
                          {hasTeam && (
                            <div className="text-xs text-blue-600 mt-0.5">+{u.teamMemberCount} team member{u.teamMemberCount === 1 ? '' : 's'}</div>
                          )}
                        </td>
                        <td className="py-3 pr-3">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              u.tierKey === 'scale' ? 'bg-purple-100 text-purple-800' :
                              u.tierKey === 'growth' ? 'bg-blue-100 text-blue-800' :
                              u.tierKey === 'starter' ? 'bg-green-100 text-green-800' :
                              u.planLabel === 'Legacy Pro' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {u.planLabel}
                            </span>
                            <StatusBadge status={u.customerStatus} />
                          </div>
                          {u.cancelAtPeriodEnd && u.customerStatus === 'active' && (
                            <div className="text-xs text-red-600 mt-1">Cancels at period end</div>
                          )}
                        </td>
                        <td className="py-3 pr-3 text-right font-medium text-gray-900">{formatPence(u.mrrPence)}</td>
                        <td className="py-3 pr-3 text-right text-gray-700">{u.projectCount}</td>
                        <td className="py-3 pr-3 text-right text-gray-700">
                          {u.monitoringQuota > 0 ? (
                            <span className={u.monitoringUtilisation && u.monitoringUtilisation > 80 ? 'text-red-600 font-medium' : ''}>
                              {formatNumber(u.monitoringUsed)} / {formatNumber(u.monitoringQuota)}
                              <div className="text-xs text-gray-400">{u.monitoringUtilisation ?? 0}%</div>
                            </span>
                          ) : '—'}
                        </td>
                        <td className="py-3 pr-3 text-right text-gray-700">{u.activeWatches}</td>
                        <td className="py-3 pr-3 text-right text-gray-700">{u.jobsCapturedThisMonth}</td>
                        <td className="py-3 pr-3 text-right text-gray-700">{u.clayEnrichmentsThisMonth}</td>
                        <td className="py-3 pr-3 text-right text-gray-600">
                          {formatDaysAgo(u.teamLastActive)}
                          {hasTeam && u.teamLastActive !== u.lastLoginAt && (
                            <div className="text-xs text-gray-400">owner: {formatDaysAgo(u.lastLoginAt)}</div>
                          )}
                        </td>
                        <td className="py-3 pr-3 text-right text-gray-600">{formatDate(u.currentPeriodEnd)}</td>
                      </tr>
                      {isExpanded && hasTeam && (
                        <tr className="bg-blue-50/40">
                          <td colSpan={11} className="px-6 py-3">
                            <div className="text-xs uppercase text-gray-500 font-medium mb-2">Team members ({u.teamMembers.length + 1} total incl. owner)</div>
                            <table className="w-full text-sm">
                              <thead className="text-left text-xs text-gray-500">
                                <tr>
                                  <th className="py-1 pr-3">Name</th>
                                  <th className="py-1 pr-3">Email</th>
                                  <th className="py-1 pr-3">Role</th>
                                  <th className="py-1 pr-3 text-right">Last active</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-blue-100">
                                {/* Owner first */}
                                <tr>
                                  <td className="py-1.5 pr-3 font-medium text-gray-900">{nameOf(u)}</td>
                                  <td className="py-1.5 pr-3 text-gray-600">{u.email}</td>
                                  <td className="py-1.5 pr-3 text-blue-700 text-xs font-medium">OWNER</td>
                                  <td className="py-1.5 pr-3 text-right text-gray-600">{formatDaysAgo(u.lastLoginAt)}</td>
                                </tr>
                                {u.teamMembers.filter(m => m.id !== u.id).map(m => (
                                  <tr key={m.id}>
                                    <td className="py-1.5 pr-3 text-gray-900">{nameOf(m)}</td>
                                    <td className="py-1.5 pr-3 text-gray-600">{m.email}</td>
                                    <td className="py-1.5 pr-3 text-gray-500 text-xs">{m.teamRole || '—'}</td>
                                    <td className={`py-1.5 pr-3 text-right ${m.lastActive ? 'text-gray-700' : 'text-gray-400'}`}>
                                      {formatDaysAgo(m.lastActive)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
};

// ============================================
// Sub-components
// ============================================

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
    {children}
  </div>
);

const KPITile: React.FC<{ label: string; value: string; sub: string; tone: 'blue' | 'green' | 'amber' | 'red' | 'gray' | 'purple' }> = ({ label, value, sub, tone }) => {
  const toneClass = {
    blue: 'border-l-blue-500',
    green: 'border-l-green-500',
    amber: 'border-l-amber-500',
    red: 'border-l-red-500',
    gray: 'border-l-gray-400',
    purple: 'border-l-purple-500'
  }[tone];
  return (
    <div className={`bg-white rounded-lg shadow p-5 border-l-4 ${toneClass}`}>
      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">{label}</p>
      <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{sub}</p>
    </div>
  );
};

const MiniRevenueCard: React.FC<{ label: string; pence: number; count: number }> = ({ label, pence, count }) => (
  <div className="border border-gray-200 rounded-lg p-4">
    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-2">{formatPence(pence)}</p>
    <p className="text-xs text-gray-500 mt-1">{count} invoice{count === 1 ? '' : 's'}</p>
  </div>
);

const MiniStat: React.FC<{ label: string; value: number; tone: 'green' | 'amber' | 'red' | 'gray' }> = ({ label, value, tone }) => {
  const toneClass = {
    green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
    gray: 'bg-gray-50 text-gray-700'
  }[tone];
  return (
    <div className={`${toneClass} rounded-lg p-4`}>
      <p className="text-xs uppercase tracking-wider font-medium opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
};

const UsageCard: React.FC<{
  label: string;
  primary: string;
  primaryLabel: string;
  rows: Array<{ label: string; value: string }>;
  footnote?: string;
}> = ({ label, primary, primaryLabel, rows, footnote }) => (
  <div className="border border-gray-200 rounded-lg p-4">
    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-2">{primary}</p>
    <p className="text-xs text-gray-500">{primaryLabel}</p>
    <div className="mt-3 space-y-1 text-sm">
      {rows.map(r => (
        <div key={r.label} className="flex justify-between text-gray-600">
          <span>{r.label}</span>
          <span className="font-medium text-gray-900">{r.value}</span>
        </div>
      ))}
    </div>
    {footnote && <p className="text-xs text-gray-400 italic mt-3">{footnote}</p>}
  </div>
);

const StatusBadge: React.FC<{ status: PayingUser['customerStatus'] }> = ({ status }) => {
  const map = {
    active:        { label: 'Active',        cls: 'bg-green-100 text-green-800 ring-1 ring-green-200' },
    trialing:      { label: 'Trial',         cls: 'bg-blue-100 text-blue-800 ring-1 ring-blue-200' },
    past_due:      { label: 'Past due',      cls: 'bg-red-100 text-red-800 ring-1 ring-red-200' },
    cancelled:     { label: 'Cancelled',     cls: 'bg-gray-200 text-gray-700 ring-1 ring-gray-300' },
    grandfathered: { label: 'Comp / free',   cls: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200' },
    unknown:       { label: '—',             cls: 'bg-gray-100 text-gray-500' }
  } as const;
  const m = map[status] || map.unknown;
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${m.cls}`}>{m.label}</span>;
};

const AttentionGroup: React.FC<{
  title: string;
  count: number;
  tone: 'red' | 'amber';
  expanded: boolean;
  onToggle: () => void;
  rows: NeedsAttentionRow[];
  renderRow: (row: NeedsAttentionRow) => React.ReactNode;
}> = ({ title, count, tone, expanded, onToggle, rows, renderRow }) => {
  const borderClass = tone === 'red' ? 'border-red-200' : 'border-amber-200';
  const dotClass = tone === 'red' ? 'bg-red-500' : 'bg-amber-500';
  return (
    <div className={`border ${borderClass} rounded-lg`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full ${dotClass}`}></span>
          <span className="font-medium text-gray-900">{title}</span>
          <span className="text-sm text-gray-500">({count})</span>
        </div>
        <span className="text-gray-400 text-sm">{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3">
          {rows.length === 0 ? (
            <p className="text-sm text-gray-500 italic">None — nice.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {rows.map(r => (
                <li key={r.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium text-gray-900">{nameOf(r)}</div>
                    <div className="text-xs text-gray-500">{r.email}</div>
                  </div>
                  <div className="text-right text-sm text-gray-600 flex flex-col items-end gap-0.5">
                    {renderRow(r)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// CRM widget — upcoming demos + tasks needing attention
// ============================================

interface CrmDashboardTask {
  id: number;
  prospect_id: number;
  title: string;
  task_type: string;
  due_date: string;
  notes: string | null;
  prospect_name: string;
  prospect_company: string | null;
  prospect_priority: number;
}

interface CrmUpcomingDemo {
  id: number;
  scheduled_at: string;
  duration_mins: number | null;
  title: string | null;
  prospect_id: number;
  prospect_name: string;
  prospect_company: string | null;
}

interface CrmDashboard {
  overdueCount:  number;
  todayCount:    number;
  upcomingCount: number;
  tasks: CrmDashboardTask[];
  upcomingDemos: CrmUpcomingDemo[];
}

const CrmWidget: React.FC = () => {
  const [data, setData] = useState<CrmDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await fetch('/api/crm/tasks/dashboard', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const completeTask = async (id: number) => {
    await fetch(`/api/crm/tasks/${id}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ completed: true })
    });
    load();
  };

  if (loading) {
    return <Section title="CRM activity"><p className="text-sm text-gray-500">Loading…</p></Section>;
  }
  if (!data) return null;

  const overdueTasks  = data.tasks.filter(t => new Date(t.due_date).getTime() < Date.now());
  const todayTasks    = data.tasks.filter(t => {
    const d = new Date(t.due_date);
    return d.getTime() >= Date.now() && d.toDateString() === new Date().toDateString();
  });
  const upcomingTasks = data.tasks.filter(t => {
    const d = new Date(t.due_date);
    return d.toDateString() !== new Date().toDateString() && d.getTime() > Date.now();
  });

  const taskFmt = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <Section title="CRM activity">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-red-700 font-semibold">Overdue tasks</p>
          <p className="text-3xl font-bold text-red-900 mt-1">{data.overdueCount}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Due today</p>
          <p className="text-3xl font-bold text-amber-900 mt-1">{data.todayCount}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-blue-700 font-semibold">Upcoming demos (14d)</p>
          <p className="text-3xl font-bold text-blue-900 mt-1">{data.upcomingDemos.length}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Tasks needing attention</h4>
          {data.tasks.length === 0 ? (
            <p className="text-sm text-gray-500 italic">All caught up. Nice.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {[...overdueTasks, ...todayTasks, ...upcomingTasks].slice(0, 12).map(t => {
                const overdue = new Date(t.due_date).getTime() < Date.now();
                return (
                  <li key={t.id} className="py-2 flex items-start gap-3 text-sm">
                    <button
                      onClick={() => completeTask(t.id)}
                      className="mt-1 w-4 h-4 border-2 border-gray-300 rounded hover:bg-green-100 hover:border-green-400 cursor-pointer shrink-0"
                      title="Mark done"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{t.title}</span>
                        {t.prospect_priority > 0 && (
                          <span className="text-[10px]" title={`Priority ${t.prospect_priority}/5`}>{'🔥'.repeat(t.prospect_priority)}</span>
                        )}
                        {t.task_type === 'follow_up' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">auto</span>
                        )}
                      </div>
                      <div className={`text-xs ${overdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                        {overdue ? 'Overdue · ' : ''}{taskFmt(t.due_date)}
                        {t.prospect_company && ` · ${t.prospect_company}`}
                      </div>
                    </div>
                    <a href="/admin/crm" className="text-xs text-blue-600 hover:underline shrink-0">view</a>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Upcoming demos (next 14 days)</h4>
          {data.upcomingDemos.length === 0 ? (
            <p className="text-sm text-gray-500 italic">Nothing booked.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.upcomingDemos.slice(0, 12).map(d => (
                <li key={d.id} className="py-2 flex items-start justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{d.prospect_name}</div>
                    <div className="text-xs text-gray-500">
                      {taskFmt(d.scheduled_at)}{d.duration_mins ? ` · ${d.duration_mins}min` : ''}
                      {d.prospect_company && ` · ${d.prospect_company}`}
                    </div>
                  </div>
                  <a href="/admin/crm" className="text-xs text-blue-600 hover:underline shrink-0">view</a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Section>
  );
};

export default CommandCenter;
